import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole, isTopTier, isSupervisorOrAbove, isAdminOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, WarningCircle, Pencil, X, UploadSimple } from "@phosphor-icons/react";
import { CertUploadDialog } from "@/components/CertUploadDialog";
import { ProfilePaperwork } from "@/components/ProfilePaperwork";

/* ============================================================
 * Member Profile — full page (/members/:id)
 *
 * The profile is the single source of truth for member operational data
 * beyond the basic account record. It renders every section BSVAC asked
 * for; sections that don't apply to the member's roles collapse
 * automatically (e.g. "Cleared to Drive" only shows for MVOs).
 *
 * The page reads the full user document from `GET /users/{id}` and writes
 * changes back via `PATCH /users/{id}/profile` — a whitelist endpoint
 * that ignores everything not in the `MemberProfileUpdate` schema, so
 * this page can't escalate privileges or clobber password hashes.
 * ========================================================== */

const AMBULANCES = ["BSVAC-2", "BSVAC-4", "BSVAC-5", "CBVAC-6", "BSVAC-7"];
const SHIRT_SIZES = ["S", "M", "L", "XL", "2X", "3X", "4X"];
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK",
  "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY", "DC",
];

// Any of these roles unlocks the Ambulance Clearance date and the Training
// tab on the profile. MVO is a distinct trigger for the "Cleared to Drive"
// section, and only clinical roles get Ambulance Clearance.
const CLINICAL_ROLES = new Set(["EMT", "EMT Intern", "Paramedic", "Paramedic Intern", "Intern"]);
const TRAINING_ELIGIBLE = new Set(["MVO", "EMT", "EMT Intern", "Paramedic", "Paramedic Intern", "Intern"]);

// iter34 — Role catalog for in-profile Edit Mode. Mirrors UsersAdmin.jsx so
// the two surfaces stay perfectly consistent. If a role is added or
// renamed, update both files.
const ALL_ROLES = ["volunteer", "supervisor", "admin", "staff_chief", "commanding_officer", "super_admin"];
const ADMIN_ASSIGNABLE_ROLES = ["volunteer", "supervisor"];
const ELEVATED_ROLES = new Set(["admin", "staff_chief", "commanding_officer", "super_admin"]);
const ROLE_META = {
  volunteer:          { label: "Volunteer",          hint: "Baseline member" },
  supervisor:         { label: "Supervisor",         hint: "Schedule + Supervisor Hub" },
  admin:              { label: "Admin",              hint: "Admin area, user management" },
  staff_chief:        { label: "Staff Chief",        hint: "Full access minus Validation" },
  commanding_officer: { label: "Commanding Officer", hint: "Full access · unrestricted" },
  super_admin:        { label: "Super Admin",        hint: "Full access · unrestricted" },
};
const DEFAULT_RESET_PW = "bsvac727";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T12:00:00").toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return d;
  }
}
/** Days-until-expiration for a YYYY-MM-DD string, negative if past. */
function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d + "T12:00:00").getTime();
  const now = Date.now();
  return Math.round((t - now) / 86400000);
}
function ExpiryTag({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  const days = daysUntil(date);
  const label = fmtDate(date);
  if (days == null) return <span className="mono text-xs">{label}</span>;
  let cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
  let hint = "";
  if (days < 0) { cls = "bg-red-50 text-red-800 border-red-200"; hint = `expired ${Math.abs(days)}d ago`; }
  else if (days < 30) { cls = "bg-amber-50 text-amber-800 border-amber-200"; hint = `${days}d left`; }
  else { hint = `${days}d left`; }
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-sm px-1.5 py-0.5 mono text-[11px] ${cls}`}>
      {days < 0 && <WarningCircle size={12} weight="fill" />}
      {label}
      <span className="opacity-60">· {hint}</span>
    </span>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <section className="surface-grid mb-5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <Label className="tab-tag">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
}

/* DriverRestrictionSection — supervisor-controlled block on MVO assignments.
 * Members on an active restriction cannot be assigned to or pick up an MVO
 * tour that overlaps the restriction window. Only supervisor+ may set or
 * remove; the member sees the current restriction state read-only. */
const DURATION_OPTIONS = ["30 Days", "60 Days", "90 Days", "Indefinite"];
function DriverRestrictionSection({ member, canEdit, onChanged }) {
  const [startDate, setStartDate] = useState("");
  const [duration, setDuration] = useState("30 Days");
  const [busy, setBusy] = useState(false);
  const current = member.driver_restriction || null;
  const isActive = !!(current && current.active);

  async function save() {
    if (!startDate) { toast.error("Restriction start date is required"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${member.id}/driver-restriction`, { start_date: startDate, duration });
      const { data } = await api.get(`/users/${member.id}`);
      onChanged(data);
      setStartDate("");
      setDuration("30 Days");
      toast.success("Driver restriction set");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set restriction");
    } finally { setBusy(false); }
  }
  async function clear() {
    setBusy(true);
    try {
      await api.delete(`/users/${member.id}/driver-restriction`);
      const { data } = await api.get(`/users/${member.id}`);
      onChanged(data);
      toast.success("Driver restriction removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove restriction");
    } finally { setBusy(false); }
  }

  return (
    <Section
      title="Driver Restriction"
      subtitle="While active, blocks the member from any MVO tour that overlaps the restriction window"
    >
      {isActive && (
        <div className="mb-3 p-3 border border-red-200 rounded-sm bg-red-50 text-sm" data-testid="pf-restriction-active">
          <div className="font-bold text-red-800 mb-1">RESTRICTED</div>
          <div className="text-red-800">
            <span className="mono">{current.start_date}</span> → {current.end_date ? <span className="mono">{current.end_date}</span> : <span className="italic">Indefinite</span>}
            {" · "}{current.duration}
          </div>
          {current.set_by_name && (
            <div className="text-[11px] text-red-700 mt-1">Set by {current.set_by_name}</div>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={clear} disabled={busy} className="rounded-sm mt-2 h-7 text-xs border-red-300 text-red-800 hover:bg-red-100" data-testid="pf-restriction-remove">
              Remove restriction
            </Button>
          )}
        </div>
      )}
      {canEdit && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl items-end">
          <Field label="Restriction Start Date" testid="pf-restriction-start">
            <Input type="date" className="rounded-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="pf-restriction-start-input" />
          </Field>
          <Field label="Duration" testid="pf-restriction-duration">
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
              data-testid="pf-restriction-duration-select"
            >
              {DURATION_OPTIONS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
          <Button type="button" onClick={save} disabled={busy || !startDate} className="rounded-sm bg-slate-900 text-white h-10 disabled:opacity-40" data-testid="pf-restriction-apply">
            {isActive ? "Replace restriction" : "Apply restriction"}
          </Button>
        </div>
      )}
      {!canEdit && !isActive && (
        <div className="text-xs text-slate-500 italic">No active driver restriction.</div>
      )}
    </Section>
  );
}

/* Downsizes any client-side image to ~600px longest edge / JPEG q=0.82
 * before it goes to the API. Keeps the base64 payload under ~200KB even
 * on a 12-megapixel phone shot. Returns a data URL. */
async function downsizeImageToDataUrl(file, maxEdge = 640, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function ProfilePhoto({ doc, isMe, onUpdated }) {
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);
  const camRef = React.useRef(null);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    setBusy(true);
    try {
      const dataUrl = await downsizeImageToDataUrl(file);
      const { data } = await api.post("/users/me/photo", { photo_data_url: dataUrl });
      toast.success("Photo updated");
      onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function removePhoto() {
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me/photo");
      toast.success("Photo removed");
      onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  }

  const initials = (doc.name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  return (
    <div className="flex flex-col items-center gap-2 shrink-0" data-testid="profile-photo">
      {doc.photo_url ? (
        <img
          src={doc.photo_url}
          alt={doc.name}
          className="w-28 h-28 rounded-sm object-cover border border-slate-200"
          data-testid="profile-photo-img"
        />
      ) : (
        <div
          className="w-28 h-28 rounded-sm border border-slate-200 bg-slate-100 flex items-center justify-center font-display font-bold text-3xl text-slate-500"
          data-testid="profile-photo-placeholder"
        >
          {initials}
        </div>
      )}
      {isMe && (
        <div className="flex flex-col gap-1 w-28">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="profile-photo-file"
          />
          <input
            ref={camRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="profile-photo-camera"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm h-7 text-[10px]"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="profile-photo-upload-btn"
          >
            Upload
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm h-7 text-[10px]"
            onClick={() => camRef.current?.click()}
            disabled={busy}
            data-testid="profile-photo-camera-btn"
          >
            Take Photo
          </Button>
          {doc.photo_url && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-sm h-6 text-[10px] text-red-600 hover:bg-red-50"
              onClick={removePhoto}
              disabled={busy}
              data-testid="profile-photo-remove-btn"
            >
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function MemberProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  // Edit Mode controls whether the profile is interactive. Starts off
  // unless the caller navigated here with `?edit=1` (used by the Admin
  // Users table "Edit" button). View Mode shows read-only text; Edit
  // Mode swaps every editable field for its input and surfaces the
  // Save / Cancel buttons. iter34.
  const [editMode, setEditMode] = useState(searchParams.get("edit") === "1");
  // Settings — needed only for the Authorized Certifications catalog
  // when editing basic info. Lazy-loaded alongside the user doc.
  const [settings, setSettings] = useState(null);
  // Class-id → class-name lookup so the "Authorized to Teach" chips can
  // render human labels instead of raw UUIDs. Fetched once alongside the
  // user doc; falls back to the ID string if the class was deleted.
  const [classMap, setClassMap] = useState({});
  // Live pull of this member's documents so the profile can show
  // approved certification/ID expirations without duplicating the field
  // on the user document.
  const [memberDocs, setMemberDocs] = useState([]);
  const [certUploadOpen, setCertUploadOpen] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  // Read-only required-classes completion for this member. Populated
  // from `/training/completion/{id}` (admin+supervisor) — members
  // cannot edit attendance status/dates; those come from the
  // instructor-submitted attendance sheets.
  const [trainingCompletion, setTrainingCompletion] = useState(null);

  const canEditBase = useMemo(() => {
    if (!currentUser) return false;
    if (!(isSupervisorOrAbove(currentUser) || isAdminOrAbove(currentUser))) return false;
    // Admin+ accounts are editable only by top tier (or the member themselves).
    const targetElevated = !!doc && (doc.is_original_admin || [doc.role, ...(doc.additional_roles || [])].some((r) => ELEVATED_ROLES.has(r)));
    return !targetElevated || isTopTier(currentUser) || doc.id === currentUser.id;
  }, [currentUser, doc]);
  // iter34 — Effective edit flag: role-based permission AND Edit Mode
  // is active. Every field-level `canEdit` gate downstream respects
  // Edit Mode automatically, so View Mode is universally read-only
  // without touching each section.
  const canEdit = canEditBase && editMode;

  // iter34 — Role assignment matrix for the in-profile edit flow.
  // Mirrors UsersAdmin.jsx exactly so the two surfaces stay consistent.
  // Elevated roles (admin, staff_chief, commanding_officer, super_admin)
  // may only be mutated by a top-tier caller; the backend
  // `_enforce_role_mutation` guard enforces the same rule server-side.
  const canAssignElevatedRoles = useMemo(() => isTopTier(currentUser), [currentUser]);
  const canAssignOperationalRoles = useMemo(
    () => canAssignElevatedRoles || hasRole(currentUser, "admin"),
    [currentUser, canAssignElevatedRoles]
  );
  const visibleRoles = canAssignElevatedRoles
    ? ALL_ROLES
    : canAssignOperationalRoles
      ? ADMIN_ASSIGNABLE_ROLES
      : [];
  const rolesInForm = useMemo(() => {
    const primary = form.role || "volunteer";
    const extras = Array.isArray(form.additional_roles) ? form.additional_roles : [];
    return Array.from(new Set([primary, ...extras].filter(Boolean)));
  }, [form.role, form.additional_roles]);
  function toggleRole(r) {
    const cur = new Set(rolesInForm);
    if (cur.has(r)) cur.delete(r);
    else cur.add(r);
    const ordered = ALL_ROLES.filter((x) => cur.has(x));
    const nextPrimary = ordered[0] || "volunteer";
    const nextExtras = ordered.slice(1);
    setForm((f) => ({ ...f, role: nextPrimary, additional_roles: nextExtras }));
    setDirty(true);
  }
  // Authoritative Authorized Certifications catalog. Exactly matches
  // what the old Edit dialog surfaced here: Supervisor, Tour Commander,
  // Student, Logistics. Legacy entries that live in the catalog ("EMT",
  // "DISPATCHER", "MVO", etc.) are retired — they belong in their own
  // dedicated profile fields (EMS Cert, Dispatcher, Driver Clearance),
  // not in this generic cert list.
  const certCatalog = ["Supervisor", "Tour Commander", "Student", "Logistics"];
  // Retired cert names that must never be sent back on save. Keeps a
  // legacy "EMT" in the user record from being persisted into the
  // Authorized Certifications array when the admin toggles anything.
  const RETIRED_CERT_NAMES = new Set(["emt", "paramedic", "mvo", "intern", "emt intern", "paramedic intern", "dispatcher", "volunteer"]);
  function toggleCert(c) {
    const cur = Array.isArray(form.certifications) ? form.certifications : [];
    // Case-insensitive match so toggling "Supervisor" doesn't duplicate
    // an existing legacy-cased "SUPERVISOR" entry; always normalize the
    // stored value to the canonical Title-Case label.
    const idx = cur.findIndex((x) => String(x || "").toLowerCase() === c.toLowerCase());
    const next = idx >= 0 ? cur.filter((_, i) => i !== idx) : [...cur.filter((x) => String(x || "").toLowerCase() !== c.toLowerCase()), c];
    setForm((f) => ({ ...f, certifications: next }));
    setDirty(true);
  }
  // Helper so the checkbox grid reflects legacy-cased values correctly.
  function hasCert(c) {
    return (form.certifications || []).some((x) => String(x || "").toLowerCase() === c.toLowerCase());
  }

  // Cancel reverts the form to the server snapshot and leaves Edit Mode.
  function cancelEdit() {
    setForm(doc || {});
    setDirty(false);
    setEditMode(false);
    // Strip the ?edit=1 from the URL so a refresh doesn't jump back in.
    if (searchParams.get("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/users/${id}`),
      api.get("/training/classes?include_inactive=true").catch(() => ({ data: [] })),
      // Documents endpoint returns everything a supervisor can see, or
      // the current user's own uploads if they're a plain member. We
      // filter to this profile's user_id client-side either way.
      api.get("/documents/").catch(() => ({ data: [] })),
      // Required-classes completion for THIS member. Only admins /
      // supervisors can pull other members' completion; regular
      // members can only pull their own. Falls back to null on
      // permission failure.
      api.get(`/training/completion/${id}`).catch(() => ({ data: null })),
      // Authorized Certifications catalog — only needed in Edit Mode
      // so Admins can tick / untick authorized certs on the profile.
      api.get("/settings").catch(() => ({ data: null })),
    ])
      .then(([u, c, d, tc, s]) => {
        if (!alive) return;
        setDoc(u.data);
        setForm(u.data);
        setClassMap(Object.fromEntries((c.data || []).map((x) => [x.id, x.name])));
        setMemberDocs((d.data || []).filter((x) => x.user_id === id));
        setTrainingCompletion(tc.data || null);
        setSettings(s.data || null);
      })
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); else toast.error(e.response?.data?.detail || "Failed to load profile"); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const memberRoles = useMemo(() => {
    if (!doc) return new Set();
    // Certifications array now holds ONLY non-EMS, non-driver credentials
    // (Dispatcher, Supervisor, Tour Commander, Logistics, Student, etc).
    // EMS cert lives in `ems_certification`; driver eligibility is derived
    // from `driver_clearance_date`. Spec iter21.
    const certs = doc.certifications?.length ? doc.certifications : [doc.certification].filter(Boolean);
    return new Set(certs);
  }, [doc]);

  // iter21 — three-axis eligibility. A member can be clinical, a driver,
  // or both. Each section renders independently so toggling driver
  // clearance never hides clinical info and vice-versa.
  const emsCert = (form.ems_certification || doc?.ems_certification || "").trim();
  const isClinical = !!emsCert || [...memberRoles].some((c) => CLINICAL_ROLES.has(c));
  // Driver section visible when the member has a clearance date (self or
  // supervisor read-only view) OR when a supervisor+ is editing (so they
  // can set the date and vehicle clearances in the first place).
  const hasDriverClearance = !!(form.driver_clearance_date || doc?.driver_clearance_date);
  // Legacy alias retained so existing render blocks don't need rewriting.
  const isMVO = hasDriverClearance || (form.cleared_to_drive || doc?.cleared_to_drive || []).some((v) => String(v || "").trim());
  const isTrainingEligible = [...memberRoles].some((c) => TRAINING_ELIGIBLE.has(c)) || !!emsCert;

  // Approved / pending docs split — the compliance snippet only shows
  // approved rows; pending count is surfaced as a subtle amber note.
  const approvedDocs = useMemo(() => memberDocs.filter((d) => d.status === "approved"), [memberDocs]);
  const pendingDocs = useMemo(() => memberDocs.filter((d) => d.status === "pending"), [memberDocs]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setDirty(true);
  }
  function toggleAmbulance(u) {
    const cur = form.cleared_to_drive || [];
    set("cleared_to_drive", cur.includes(u) ? cur.filter((x) => x !== u) : [...cur, u]);
  }

  async function save() {
    if (!dirty) return;
    setSaving(true);
    try {
      // iter34 — Two-request save: PATCH /users/{id} for the base
      // account fields (name, email, phone, role, authorized certs,
      // optional password reset) and PATCH /users/{id}/profile for the
      // whitelisted operational fields below. We send a base PATCH only
      // when at least one of those fields actually changed to keep the
      // audit log clean and avoid tripping the elevated-role mutation
      // guard on no-op saves.
      const baseChanged =
        (form.name || "") !== (doc.name || "") ||
        (form.email || "").toLowerCase() !== (doc.email || "").toLowerCase() ||
        (form.phone || "") !== (doc.phone || "") ||
        (form.role || "") !== (doc.role || "") ||
        JSON.stringify(form.additional_roles || []) !== JSON.stringify(doc.additional_roles || []) ||
        JSON.stringify(form.certifications || []) !== JSON.stringify(doc.certifications || []) ||
        !!form._password_reset;
      if (baseChanged && canEdit) {
        // When the caller cannot touch elevated roles, make sure any
        // elevated roles already on the record are preserved verbatim
        // so a basic info save doesn't accidentally strip them.
        let nextRole = form.role || "volunteer";
        let nextExtras = Array.isArray(form.additional_roles) ? form.additional_roles.filter((r) => r && r !== nextRole) : [];
        if (!canAssignElevatedRoles) {
          const preserved = [doc.role, ...(doc.additional_roles || [])]
            .filter((r) => r && ELEVATED_ROLES.has(r));
          // If the record was elevated, keep the primary role exactly
          // where it is — Admin must never flip a Staff Chief to
          // Volunteer via this surface.
          if (ELEVATED_ROLES.has(doc.role)) {
            nextRole = doc.role;
            const merged = new Set([...(nextExtras || []), ...preserved]);
            merged.delete(nextRole);
            nextExtras = Array.from(merged);
          } else {
            const operational = [nextRole, ...nextExtras].filter((r) => r && ADMIN_ASSIGNABLE_ROLES.includes(r));
            const merged = new Set([...operational, ...preserved]);
            const ordered = ALL_ROLES.filter((r) => merged.has(r));
            nextRole = ordered[0] || "volunteer";
            nextExtras = ordered.slice(1);
          }
        }
        const basePayload = {
          name: form.name,
          email: (form.email || "").trim(),
          phone: form.phone || "",
          role: nextRole,
          additional_roles: nextExtras,
          // Strip legacy / retired names on save so a stale "EMT" or
          // "DISPATCHER" that lingered in the certifications array is
          // cleaned up on the next admin save — those credentials
          // belong in their own dedicated profile fields now. Also
          // normalize any mis-cased legacy entry ("SUPERVISOR",
          // "LOGSTICS") to the canonical Title-Case label from
          // `certCatalog` so the UI stays consistent.
          certifications: (form.certifications || [])
            .filter((c) => !RETIRED_CERT_NAMES.has(String(c || "").trim().toLowerCase()))
            .map((c) => (/^(logstics|logisitics|logistic)$/i.test(String(c || "").trim()) ? "Logistics" : c))
            .map((c) => certCatalog.find((x) => x.toLowerCase() === String(c || "").toLowerCase()) || c),
        };
        if (form._password_reset) basePayload.password = form._password_reset;
        await api.patch(`/users/${id}`, basePayload);
      }

      // Only send the profile-scoped subset — the whitelist on the server
      // will drop anything else, but keeping the payload tight avoids
      // shipping the entire user document over the wire on every save.
      // Sections that don't apply (MVO / clinical) are intentionally
      // omitted rather than sent-as-null so revoking a certification
      // doesn't silently wipe historical clearance data.
      const payload = {
        // Non-expiring clearances kept on the profile
        medical_clearance_date: form.medical_clearance_date || null,
        nys_igel_cert_date: form.nys_igel_cert_date || null,
        // Driver's license — state + number stay on the profile; the
        // expiration lives on the uploaded ID document instead.
        dl_state: form.dl_state || null,
        dl_number: form.dl_number || null,
        // Seniority + uniform stay on the profile.
        seniority_start_date: form.seniority_start_date || null,
        seniority_end_date: form.seniority_end_date || null,
        shirt_size: form.shirt_size || null,
        shirt_received_date: form.shirt_received_date || null,
        hat_issued: !!form.hat_issued,
        hat_received_date: form.hat_received_date || null,
      };
      // iter34.3 — ambulance_clearance_date retired. Clinical clearance
      // (N/I/C/C+/FTO) now carries ride-eligibility. We intentionally
      // do NOT send this field anymore; the backend still accepts it on
      // the model for backward compatibility but will stop surfacing it.
      // Driver fields: always included when the supervisor is editing so
      // setting the clearance date for the first time persists (there's
      // no "isDriver" short-circuit before the save that would skip it).
      if (canEdit || isMVO) {
        payload.cleared_to_drive = form.cleared_to_drive || [];
        payload.driver_clearance_date = form.driver_clearance_date || null;
      }
      // EMS Certification is an independent axis — EMT, Paramedic, or
      // empty. Sent on every save; backend keeps `certifications` in
      // sync so required-docs stays correct.
      if (canEdit && (form.ems_certification !== undefined || doc?.ems_certification !== undefined)) {
        payload.ems_certification = form.ems_certification || "";
      }
      // New fields (specs #6, #7, #8, #10). Only include when they
      // actually changed from the server snapshot so a plain re-save
      // doesn't overwrite a supervisor's edits in a race.
      // iter22 — When EMS Cert is None we must NOT send clinical
      // clearance fields on save (backend 400s otherwise). The existing
      // DB values are preserved as history.
      if (isClinical && emsCert) {
        payload.clearance_status = form.clearance_status || "N";
        payload.clearance_c_date = form.clearance_c_date || null;
        payload.clearance_i_date = form.clearance_i_date || null;
        payload.clearance_cplus_date = form.clearance_cplus_date || null;
        payload.clearance_fto_date = form.clearance_fto_date || null;
      }
      payload.dispatcher_status = form.dispatcher_status || null;
      payload.dispatcher_number = form.dispatcher_number || null;
      payload.dispatcher_clearance_date = form.dispatcher_clearance_date || null;
      if (canEdit && typeof form.supervisor_comments === "string") {
        payload.supervisor_comments = form.supervisor_comments;
      }
      if (form.member_id && Number(form.member_id) !== Number(doc.member_id)) {
        payload.member_id = Number(form.member_id);
      }
      const { data } = await api.patch(`/users/${id}/profile`, payload);
      // Re-fetch the full doc so basic info updates (from the first
      // PATCH) and profile updates (from the second) merge back into
      // state cleanly.
      const fresh = baseChanged ? (await api.get(`/users/${id}`)).data : data;
      setDoc(fresh);
      setForm({ ...fresh, _password_reset: undefined });
      setDirty(false);
      toast.success("Profile saved");
      // Leave Edit Mode after a successful save per spec — the user
      // stays on the profile and sees the fresh View Mode immediately.
      setEditMode(false);
      if (searchParams.get("edit")) {
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <AppLayout title="Member Profile"><div className="p-6 text-sm text-slate-500">Loading…</div></AppLayout>;
  }
  if (!doc) {
    return <AppLayout title="Member Profile"><div className="p-6 text-sm text-red-600" data-testid="member-load-error">{forbidden ? "You don't have permission to view this member's profile." : "Member not found."}</div></AppLayout>;
  }

  const trainingHistory = Array.isArray(doc.training_history) ? doc.training_history : [];
  const instructorClasses = Array.isArray(doc.instructor_classes) ? doc.instructor_classes : [];

  return (
    <AppLayout
      title={doc.name}
      subtitle={<span className="flex items-center gap-2">
        {doc.member_id && <span className="mono text-xs bg-slate-900 text-white px-1.5 py-0.5 rounded-sm" data-testid="member-id">#{doc.member_id}</span>}
        <span>{doc.role || "volunteer"} · {doc.email}</span>
        {["EMT", "Paramedic"].includes((doc.ems_certification || "").trim()) && doc.clearance_status && <span className="mono text-xs bg-emerald-700 text-white px-1.5 py-0.5 rounded-sm" data-testid="member-clearance">{doc.clearance_status}</span>}
      </span>}
    >
      {/* Sticky action bar so Save / Edit controls are always in reach
          on long profiles. iter34 — toggles between View Mode (Edit
          button) and Edit Mode (Save + Cancel). */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-2 mb-4 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900"
          data-testid="member-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-2">
          {editMode && dirty && <span className="text-[11px] text-amber-700 mono" data-testid="member-dirty-note">unsaved changes</span>}
          {(currentUser?.id === id || canEditBase) && (
            <Button variant="outline" onClick={() => setCertUploadOpen(true)} className="rounded-sm border-slate-300" data-testid="member-upload-cert">
              <UploadSimple size={14} weight="bold" className="mr-1" /> Upload Certification
            </Button>
          )}
          {canEditBase && !editMode && (
            <Button
              onClick={() => setEditMode(true)}
              className="rounded-sm bg-slate-900 text-white"
              data-testid="member-enter-edit"
            >
              <Pencil size={14} weight="bold" className="mr-1" /> Edit Profile
            </Button>
          )}
          {canEditBase && editMode && (
            <>
              <Button
                variant="outline"
                onClick={cancelEdit}
                disabled={saving}
                className="rounded-sm border-slate-300 text-slate-700"
                data-testid="member-cancel"
              >
                <X size={14} weight="bold" className="mr-1" /> Cancel
              </Button>
              <Button
                onClick={save}
                disabled={!dirty || saving}
                className="rounded-sm bg-slate-900 text-white disabled:opacity-40"
                data-testid="member-save"
              >
                <FloppyDisk size={14} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ============ Basic info ============
       * iter34 — When Edit Mode is on, the four core account fields
       * (name, email, phone, role) swap to live inputs plus the Admin's
       * password-reset shortcut. View Mode shows the same data read-only.
       * Authorized Certifications live below in their own section when
       * editing; the chip row here always shows what's live on the record. */}
      <Section
        title="Basic Information"
        subtitle={editMode ? "Editing · Save Changes to persist" : "Account & rank"}
      >
        <div className="flex flex-col md:flex-row gap-6">
          <ProfilePhoto
            doc={doc}
            isMe={currentUser?.id === doc.id}
            onUpdated={(updated) => {
              // The endpoint returns the public _public() shape, which
              // doesn't carry the operational fields (seniority, etc.).
              // Patch just the photo so we don't wipe those.
              setDoc((d) => ({ ...d, photo_url: updated.photo_url }));
              setForm((f) => ({ ...f, photo_url: updated.photo_url }));
            }}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm flex-1">
            {/* NAME */}
            <div>
              <div className="tab-tag text-slate-500">Name</div>
              {editMode && canEditBase ? (
                <Input
                  value={form.name || ""}
                  onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setDirty(true); }}
                  className="rounded-sm h-9 mt-1"
                  data-testid="pf-basic-name"
                />
              ) : (
                <div className="font-medium">{doc.name}</div>
              )}
            </div>
            {/* EMAIL */}
            <div>
              <div className="tab-tag text-slate-500">Email</div>
              {editMode && canEditBase ? (
                <Input
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setDirty(true); }}
                  className="rounded-sm h-9 mt-1 mono text-xs"
                  data-testid="pf-basic-email"
                />
              ) : (
                <div className="mono text-xs">{doc.email}</div>
              )}
            </div>
            {/* PHONE */}
            <div>
              <div className="tab-tag text-slate-500">Phone</div>
              {editMode && canEditBase ? (
                <Input
                  value={form.phone || ""}
                  onChange={(e) => { setForm((f) => ({ ...f, phone: e.target.value })); setDirty(true); }}
                  placeholder="555-1234"
                  className="rounded-sm h-9 mt-1 mono text-xs"
                  data-testid="pf-basic-phone"
                />
              ) : (
                <div className="mono text-xs">{doc.phone || "—"}</div>
              )}
            </div>
            {/* ROLE(S) — read-only chips always; multi-select grid lives
                in its own section below when editing. */}
            <div>
              <div className="tab-tag text-slate-500">Role</div>
              <div>
                <Badge variant="outline" className="rounded-sm mr-1">{doc.role}</Badge>
                {(doc.additional_roles || []).map((r) => <Badge key={r} variant="outline" className="rounded-sm mr-1">{r}</Badge>)}
              </div>
            </div>
            <div>
              <div className="tab-tag text-slate-500">Certifications / Rank</div>
              <div className="flex flex-wrap gap-1">
                {memberRoles.size ? [...memberRoles].map((c) => <Badge key={c} className="rounded-sm bg-slate-900 text-white">{c}</Badge>) : <span className="text-slate-400">—</span>}
              </div>
            </div>
            <div><div className="tab-tag text-slate-500">Status</div><div>{doc.active === false ? <Badge className="rounded-sm bg-slate-400 text-white">Inactive</Badge> : <Badge className="rounded-sm bg-emerald-600 text-white">Active</Badge>}</div></div>
          </div>
        </div>

        {/* Edit Mode extras: Role multi-select + Authorized Certifications
            + password reset. Hidden entirely in View Mode so the chrome
            stays minimal. */}
        {editMode && canEditBase && (
          <div className="mt-6 pt-5 border-t border-slate-200 space-y-5">
            {/* Role grid */}
            {visibleRoles.length > 0 && (
              <div>
                <div className="tab-tag text-slate-500 mb-1">
                  Roles
                  {!canAssignElevatedRoles && canAssignOperationalRoles && (
                    <span className="normal-case tracking-normal text-slate-500 font-normal"> · you may assign Volunteer and Supervisor (Logistics is a cert)</span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mb-2">Check every role this member should have. Permissions from each role stack together.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="pf-roles-grid">
                  {visibleRoles.map((r) => (
                    <label key={r} className="flex items-start gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer" data-testid={`pf-role-${r}`}>
                      <Checkbox
                        checked={rolesInForm.includes(r)}
                        onCheckedChange={() => toggleRole(r)}
                        className="mt-0.5"
                      />
                      <div className="leading-tight">
                        <div className="capitalize font-medium text-slate-900">{ROLE_META[r]?.label || r.replace(/_/g, " ")}</div>
                        <div className="text-[10px] text-slate-500">{ROLE_META[r]?.hint || ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {!canAssignElevatedRoles && (() => {
                  const preserved = [doc.role, ...(doc.additional_roles || [])]
                    .filter((r) => r && ELEVATED_ROLES.has(r));
                  if (!preserved.length) return null;
                  return (
                    <div className="mt-2 border border-amber-300 bg-amber-50 rounded-sm px-2 py-1.5 text-[11px] text-amber-900" data-testid="pf-elevated-locked">
                      <span className="font-bold">Preserved on save:</span>{" "}
                      {preserved.map((r) => ROLE_META[r]?.label || r).join(", ")}
                      {" — "}only a Staff Chief / Commanding Officer / Super Admin can change elevated roles.
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Authorized Certifications */}
            {certCatalog.length > 0 && (
              <div>
                <div className="tab-tag text-slate-500 mb-1">Authorized Certifications</div>
                <p className="text-[11px] text-slate-500 mb-2">Non-EMS, non-driver credentials. "Logistics" grants the Logistics section.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="pf-certs-grid">
                  {certCatalog.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer" data-testid={`pf-cert-${c}`}>
                      <Checkbox
                        checked={hasCert(c)}
                        onCheckedChange={() => toggleCert(c)}
                      />
                      <span className="capitalize text-slate-900">{c}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Password reset */}
            <div>
              <div className="tab-tag text-slate-500 mb-1">Reset Password</div>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Leave blank to keep current password"
                  value={form._password_reset || ""}
                  onChange={(e) => { setForm((f) => ({ ...f, _password_reset: e.target.value })); setDirty(true); }}
                  className="rounded-sm h-9 max-w-xs"
                  data-testid="pf-basic-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setForm((f) => ({ ...f, _password_reset: DEFAULT_RESET_PW })); setDirty(true); }}
                  className="rounded-sm h-9 text-xs border-slate-300 text-slate-700"
                  data-testid="pf-basic-password-default"
                >Use default ({DEFAULT_RESET_PW})</Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">On save, the member will be prompted to change their password at next sign in.</p>
            </div>
          </div>
        )}
      </Section>

      {/* ============ Seniority ============ */}
      <Section title="Seniority" subtitle="Date the member was added to the system">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <Field label="Date Added to System" testid="pf-sen-added">
            <div className="h-10 flex items-center px-2 border border-slate-200 rounded-sm bg-slate-50 text-sm mono" data-testid="pf-sen-added-val">{doc.created_at ? new Date(doc.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "America/New_York" }) : "—"}</div>
          </Field>
          <Field label="End Date (optional)" hint="Leave blank while member is active" testid="pf-sen-end">
            <Input type="date" value={form.seniority_end_date || ""} onChange={(e) => set("seniority_end_date", e.target.value)} disabled={!canEdit} className="rounded-sm" />
          </Field>
        </div>
      </Section>

      {/* ============ Uniform ============ */}
      <Section title="Uniforms" subtitle="Shirt and hat issued to member">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-3">
            <div className="tab-tag">Shirt</div>
            <Field label="Size" testid="pf-shirt-size">
              <select
                value={form.shirt_size || ""}
                onChange={(e) => set("shirt_size", e.target.value)}
                disabled={!canEdit}
                className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white disabled:bg-slate-100"
              >
                <option value="">—</option>
                {SHIRT_SIZES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Date Received" testid="pf-shirt-date">
              <Input type="date" value={form.shirt_received_date || ""} onChange={(e) => set("shirt_received_date", e.target.value)} disabled={!canEdit} className="rounded-sm bg-white" />
            </Field>
          </div>
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-3">
            <div className="tab-tag">Hat</div>
            <label className="flex items-center gap-2 text-sm" data-testid="pf-hat-issued">
              <Checkbox
                checked={!!form.hat_issued}
                onCheckedChange={(v) => canEdit && set("hat_issued", !!v)}
                disabled={!canEdit}
              />
              <span>Hat issued</span>
            </label>
            <Field label="Date Received" testid="pf-hat-date">
              <Input type="date" value={form.hat_received_date || ""} onChange={(e) => set("hat_received_date", e.target.value)} disabled={!canEdit || !form.hat_issued} className="rounded-sm bg-white disabled:bg-slate-100" />
            </Field>
          </div>
        </div>
      </Section>

      {/* ============ EMS Certification (iter21) ============ */}
      <Section title="Certification" subtitle="EMS certification — independent of clinical clearance and driver clearance">
        <div className="flex flex-wrap gap-2 max-w-md" data-testid="pf-ems-cert-grid">
          {[
            { v: "", label: "None" },
            { v: "EMT", label: "EMT" },
            { v: "Paramedic", label: "Paramedic" },
          ].map((o) => {
            const active = (form.ems_certification || "") === o.v;
            return (
              <button
                key={o.v || "none"}
                type="button"
                disabled={!canEdit}
                onClick={() => set("ems_certification", o.v)}
                className={`border rounded-sm px-4 py-2 text-sm transition-colors ${active ? "border-slate-900 bg-slate-900 text-white font-bold" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                data-testid={`pf-ems-cert-${o.v || "none"}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-slate-500 italic mt-2">
          Required paperwork follows this certification. Intern status is controlled via Clinical Clearance below.
        </div>
      </Section>

      {/* ============ Clinical Clearance ============
       * iter22 — Rendered even when EMS Cert = None, but grayed out and
       * disabled. The clinical values stay in the DB for history, but
       * eligibility logic treats the member as having no clinical
       * clearance until an EMS cert is set again. */}
      {isClinical && (() => {
        const emsNone = !emsCert;
        const credLabel = emsCert === "Paramedic" ? "Paramedic" : (emsCert === "EMT" ? "EMT" : "Clinical");
        return (
        <Section
          title="Clinical Clearance"
          subtitle={emsNone
            ? "Disabled while EMS Certification is None. Historical values preserved."
            : "One current clearance with one associated date. Dates auto-populate on status change; supervisors may correct them."}
        >
          {emsNone && (
            <div className="mb-3 p-2.5 border border-amber-300 bg-amber-50 text-amber-900 rounded-sm text-xs" data-testid="pf-clinical-disabled">
              Clinical clearance is unavailable because this member's EMS Certification is set to None.
              Select EMT or Paramedic above to enable clinical scheduling eligibility.
            </div>
          )}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl ${emsNone ? "opacity-50 pointer-events-none select-none" : ""}`} aria-disabled={emsNone}>
            <div>
              <Label className="tab-tag mb-1 block">Current Clearance{emsCert ? ` — ${credLabel}` : ""}</Label>
              <div className="grid grid-cols-5 gap-1" data-testid="pf-clearance-grid">
                {[
                  { code: "N",   label: "Not Cleared" },
                  { code: "I",   label: emsCert === "Paramedic" ? "Paramedic Intern" : "Intern" },
                  { code: "C",   label: "Cleared <90d" },
                  { code: "C+",  label: "Cleared 90d+" },
                  { code: "FTO", label: "FTO" },
                ].map((o) => {
                  const active = (form.clearance_status || "N") === o.code;
                  return (
                    <button
                      key={o.code}
                      type="button"
                      disabled={!canEdit || emsNone}
                      onClick={() => {
                        set("clearance_status", o.code);
                        const today = new Date().toISOString().slice(0, 10);
                        const map = { I: "clearance_i_date", C: "clearance_c_date", "C+": "clearance_cplus_date", FTO: "clearance_fto_date" };
                        const fld = map[o.code];
                        if (fld && !form[fld]) set(fld, today);
                      }}
                      className={`border rounded-sm px-2 py-2 text-xs text-center transition-colors ${active ? "border-slate-900 bg-slate-900 text-white font-bold" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                      data-testid={`pf-clearance-${o.code}`}
                    >
                      <div className="mono text-sm font-bold">{o.code}</div>
                      <div className="text-[10px] opacity-80 mt-0.5">{o.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              {form.clearance_status === "I" && (
                <Field label="Cleared" testid="pf-clearance-i-date">
                  <Input type="date" className="rounded-sm" value={form.clearance_i_date || ""}
                    onChange={(e) => set("clearance_i_date", e.target.value)} disabled={!canEdit || emsNone} />
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Date the member was cleared to intern. Status remains {emsCert === "Paramedic" ? "Paramedic Intern" : "Intern"}.
                  </div>
                </Field>
              )}
              {form.clearance_status === "C" && (
                <Field label={`Date — Cleared (${credLabel}: C)`} testid="pf-clearance-c-date">
                  <Input type="date" className="rounded-sm" value={form.clearance_c_date || ""}
                    onChange={(e) => set("clearance_c_date", e.target.value)} disabled={!canEdit || emsNone} />
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Automatically rolls to C+ 90 days after this date.
                  </div>
                </Field>
              )}
              {form.clearance_status === "C+" && (
                <Field label={`Date — ${credLabel} Cleared 90+ (C+)`} testid="pf-clearance-cplus-date">
                  <Input type="date" className="rounded-sm" value={form.clearance_cplus_date || ""}
                    onChange={(e) => set("clearance_cplus_date", e.target.value)} disabled={!canEdit || emsNone} />
                  {form.clearance_c_date && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Original C clearance: {fmtDate(form.clearance_c_date)} (preserved in history)
                    </div>
                  )}
                </Field>
              )}
              {form.clearance_status === "FTO" && (
                <Field label={`Date — ${credLabel} FTO`} testid="pf-clearance-fto-date">
                  <Input type="date" className="rounded-sm" value={form.clearance_fto_date || ""}
                    onChange={(e) => set("clearance_fto_date", e.target.value)} disabled={!canEdit || emsNone} />
                </Field>
              )}
              {form.clearance_status === "N" && (
                <div className="text-[11px] text-slate-500 italic">
                  Not Cleared uses the member's system creation date; no editable date needed.
                </div>
              )}
            </div>
          </div>
        </Section>
      ); })()}

      {/* ============ Dispatcher ============ */}
      <Section title="Dispatch" subtitle="Dispatcher clearance alone controls dispatcher scheduling eligibility">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <Field label="Dispatcher Status" testid="pf-dispatcher-status">
            <select
              value={form.dispatcher_status || ""}
              onChange={(e) => {
                const v = e.target.value;
                set("dispatcher_status", v);
                // Auto-populate today when supervisor clears the member
                // and no date has been recorded. Supervisors can override
                // the date below.
                if (v === "Cleared" && !form.dispatcher_clearance_date) {
                  set("dispatcher_clearance_date", new Date().toISOString().slice(0, 10));
                }
              }}
              disabled={!canEdit}
              className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
              data-testid="pf-dispatcher-status-input"
            >
              <option value="">— Not set —</option>
              <option value="Cleared">Cleared</option>
              <option value="Not Cleared">Not Cleared</option>
            </select>
          </Field>
          <Field label="Dispatcher Clearance Date" testid="pf-dispatcher-clear-date">
            <Input
              type="date"
              value={form.dispatcher_clearance_date || ""}
              onChange={(e) => set("dispatcher_clearance_date", e.target.value)}
              disabled={!canEdit || form.dispatcher_status !== "Cleared"}
              className="rounded-sm"
              data-testid="pf-dispatcher-clear-date-input"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">
              Auto-set when supervisor marks Cleared. Preserved as history when revoked.
            </div>
          </Field>
          <Field label="Dispatcher Number" testid="pf-dispatcher-number">
            <Input
              value={form.dispatcher_number || ""}
              onChange={(e) => set("dispatcher_number", e.target.value)}
              disabled={!canEdit}
              className="rounded-sm"
              data-testid="pf-dispatcher-number-input"
              placeholder="e.g. 42"
            />
          </Field>
        </div>
      </Section>

      {/* Ambulance Clearance removed in iter34.3 — clinical clearance
          (N/I/C/C+/FTO) now carries the authority to ride on the
          ambulance. The old free-form date was redundant. */}

      {/* ============ Vehicle Clearance (Driver) ============ */}
      {(isMVO || canEdit) && (
        <Section title="Driver's Clearance" subtitle="Driver clearance and vehicles the member is authorized to operate">
          <div className="space-y-3">
            <Field label="Driver Clearance Date" testid="pf-driver-clear-date">
              <Input
                type="date"
                className="rounded-sm max-w-xs"
                value={form.driver_clearance_date || ""}
                onChange={(e) => set("driver_clearance_date", e.target.value)}
                disabled={!canEdit}
                data-testid="pf-driver-clear-date-input"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">
                Date this member was cleared to drive. Setting this enables MVO assignment eligibility.
              </div>
            </Field>
            <div>
              <Label className="tab-tag mb-1 block">Vehicles Cleared</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 max-w-xl">
                {AMBULANCES.map((u) => (
                  <label
                    key={u}
                    className={`flex items-center gap-2 text-sm border rounded-sm px-2 py-2 cursor-pointer ${(form.cleared_to_drive || []).includes(u) ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:border-slate-400"}`}
                    data-testid={`pf-drive-${u}`}
                  >
                    <Checkbox
                      checked={(form.cleared_to_drive || []).includes(u)}
                      onCheckedChange={() => canEdit && toggleAmbulance(u)}
                      disabled={!canEdit}
                    />
                    <span className="mono text-xs">{u}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ============ Driver Restriction ============ */}
      {(isMVO || canEdit) && (
        <DriverRestrictionSection
          member={doc}
          canEdit={canEdit}
          onChanged={(updatedUser) => { setDoc(updatedUser); setForm(updatedUser); }}
        />
      )}

      {/* Every member has at least Dispatch as a required class, so
          render the section whenever backend returned completion
          data. Falls back to the old certification-driven gate for
          safety on error. */}
      {(trainingCompletion?.classes?.length || isTrainingEligible) && (
        <Section
          title="BSVAC Training"
          subtitle="Required classes for this member. Attendance is submitted by instructors and cannot be edited here."
          right={<Link to="/training" className="text-[11px] text-blue-700 underline">Open Training →</Link>}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="tab-tag">Required Classes</div>
                {trainingCompletion && (
                  <span className="text-[11px] mono text-slate-500" data-testid="pf-training-pct">
                    {trainingCompletion.completed_count}/{trainingCompletion.required_count} · {trainingCompletion.percent_complete}%
                  </span>
                )}
              </div>
              {!trainingCompletion || (trainingCompletion.classes || []).length === 0 ? (
                <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-3">
                  No required classes for this classification yet.
                </div>
              ) : (
                <ul className="space-y-1 text-sm" data-testid="pf-required-classes">
                  {trainingCompletion.classes.map((c) => (
                    <li
                      key={c.class_id}
                      className={`flex items-center justify-between border rounded-sm px-2 py-1.5 ${c.completed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
                      data-testid={`pf-required-class-${c.class_id}`}
                    >
                      <span className="flex items-center gap-2">
                        <Checkbox checked={!!c.completed} disabled />
                        <span className={`font-medium ${c.completed ? "text-slate-900" : "text-red-800"}`}>{c.class_name}</span>
                      </span>
                      <span className={`mono text-[11px] ${c.completed ? "text-slate-600" : "text-red-700 font-bold"}`}>
                        {c.completed ? fmtDate(c.date) : "OUTSTANDING"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {trainingCompletion && (trainingCompletion.outstanding || []).length > 0 && (
                <div className="mt-2 text-[11px] text-red-700" data-testid="pf-outstanding-summary">
                  Outstanding: {trainingCompletion.outstanding.join(", ")}
                </div>
              )}
            </div>
            <div>
              <div className="tab-tag mb-2">Authorized to Teach</div>
              {instructorClasses.length === 0 ? (
                <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-3">
                  Not currently an instructor. Add this member from Training → Instructors to authorize teaching classes.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {instructorClasses.map((cid) => <Badge key={cid} className="rounded-sm bg-slate-900 text-white">{classMap[cid] || cid}</Badge>)}
                </div>
              )}
            </div>
          </div>
        </Section>
      )}
      {/* ============ Paperwork ============ */}
      <ProfilePaperwork memberId={id} isSelf={currentUser?.id === id} refreshKey={memberDocs.length} />

      {/* ============ Supervisor Comments (supervisor+ only) ============ */}
      {canEdit && (
        <Section
          title="Supervisor Comments"
          subtitle="Visible to supervisors and above only — never shown to the member on their own profile"
        >
          <textarea
            className="w-full border border-slate-200 rounded-sm p-2 min-h-[100px] text-sm font-mono"
            value={form.supervisor_comments || ""}
            onChange={(e) => set("supervisor_comments", e.target.value)}
            placeholder="Private notes about this member — call-outs, follow-ups, performance observations…"
            data-testid="pf-supervisor-comments"
          />
        </Section>
      )}

      <CertUploadDialog
        open={certUploadOpen}
        targetUser={doc && doc.id !== currentUser?.id ? doc : null}
        onClose={() => setCertUploadOpen(false)}
        onDone={() => api.get("/documents/").then((d) => setMemberDocs((d.data || []).filter((x) => x.user_id === id))).catch(() => {})}
      />
    </AppLayout>
  );
}
